import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal";
import {
  useDeleteDriverExpenseMutation,
  useGetDriverExpenses,
} from "../../../../api/fleet-management/driver-expenses/driver-expenses.hooks";
import DriverExpensesHeader from "./_components/DriverExpensesHeader";
import DriverExpensesTable from "./_components/DriverExpensesTable";
import {
  filterDriverExpenses,
  normalizeDriverExpenses,
} from "./utils/driver-expense.utils";

const DriverExpenses = () => {
  const history = useHistory();
  const { data, isLoading, isError, error } = useGetDriverExpenses();
  const { mutate: deleteDriverExpense, isLoading: isDeleting } = useDeleteDriverExpenseMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const expenses = normalizeDriverExpenses(data);

  const filteredExpenses = useMemo(() => {
    return filterDriverExpenses(expenses, searchTerm);
  }, [expenses, searchTerm]);

  const handleEdit = (driverExpenseId) => {
    history.push(`/fleet-management/driver-expenses/update/${driverExpenseId}`);
  };

  const openDeleteModal = (expense) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedExpense(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedExpense?._id) return;

    setFeedback({ type: "", message: "" });
    deleteDriverExpense(selectedExpense._id, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Driver expense deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete driver expense.",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load driver expenses. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <DriverExpensesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredExpenses.length}
          onAddDriverExpense={() => history.push("/fleet-management/driver-expenses/add")}
        />

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredExpenses.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No driver expenses found</p>
            </div>
          ) : (
            <DriverExpensesTable
              expenses={filteredExpenses}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Driver Expense"
        message={`Are you sure you want to delete expense for ${selectedExpense?.driverId?.name || "this driver"}? This action cannot be undone.`}
        confirmText="Delete Driver Expense"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DriverExpenses;
